import { useQuery } from '@tanstack/react-query'
import { Navigate, Route, Routes } from 'react-router-dom'
import { Placeholder, Spinner } from '@telegram-apps/telegram-ui'

import { api } from './lib/api'
import type { Me } from './lib/types'

import TabBarDirector from './components/TabBarDirector'
import TabBarSecretary from './components/TabBarSecretary'
import TabBarDriver from './components/TabBarDriver'

import DirectorDashboard from './pages/director/Dashboard'
import DirectorTasks from './pages/director/Tasks'
import DirectorTaskDetail from './pages/director/TaskDetail'
import DirectorCreateTask from './pages/director/CreateTask'
import DirectorCalendar from './pages/director/Calendar'
import DirectorFacts from './pages/director/Facts'
import DirectorSettings from './pages/director/Settings'

import SecretaryStats from './pages/secretary/Stats'

import AssigneeActive from './pages/shared/AssigneeActive'
import AssigneeDone from './pages/shared/AssigneeDone'
import AssigneeTaskDetail from './pages/shared/AssigneeTaskDetail'
import MyCalendar from './pages/shared/MyCalendar'

export default function App() {
  const { data: meReal, isLoading, error } = useQuery<Me>({
    queryKey: ['me'],
    queryFn: () => api.get<Me>('/me'),
    retry: 0,
  })

  const devRoleParam = new URLSearchParams(window.location.search).get('role')
  const devRole: Me['role'] =
    devRoleParam === 'secretary' ? 'secretary' :
    devRoleParam === 'driver' ? 'driver' : 'director'
  const devFallback: Me | null = import.meta.env.DEV
    ? {
        role: devRole,
        user_id: 0,
        name: 'Dev',
        director_name: 'Александр Юрьевич',
        secretary_name: 'Анастасия',
        driver_name: 'Владимир',
      }
    : null
  const me = meReal ?? devFallback

  if (isLoading && !import.meta.env.DEV) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <Spinner size="l" />
      </div>
    )
  }

  if (!me) {
    return (
      <Placeholder
        header="Доступ закрыт"
        description={
          error instanceof Error
            ? error.message
            : 'Не удалось получить ваш профиль. Откройте приложение из бота.'
        }
      />
    )
  }

  return (
    <div className="app-layout">
      {me.is_admin && (
        <div className="admin-banner">
          Режим администратора · данные не показываются · изменения недоступны
        </div>
      )}
      <div className="page">
        {me.role === 'director' && (
          <Routes>
            <Route path="/" element={<DirectorDashboard />} />
            <Route path="/tasks" element={<DirectorTasks />} />
            <Route path="/tasks/new" element={<DirectorCreateTask />} />
            <Route path="/tasks/:id" element={<DirectorTaskDetail />} />
            <Route path="/calendar" element={<DirectorCalendar />} />
            <Route path="/facts" element={<DirectorFacts />} />
            <Route path="/settings" element={<DirectorSettings />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}

        {me.role === 'secretary' && (
          <Routes>
            <Route path="/" element={<AssigneeActive />} />
            <Route path="/done" element={<AssigneeDone />} />
            <Route path="/stats" element={<SecretaryStats />} />
            <Route path="/calendar" element={<MyCalendar />} />
            <Route path="/tasks/:id" element={<AssigneeTaskDetail />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}

        {me.role === 'driver' && (
          <Routes>
            <Route path="/" element={<AssigneeActive />} />
            <Route path="/done" element={<AssigneeDone />} />
            <Route path="/calendar" element={<MyCalendar />} />
            <Route path="/tasks/:id" element={<AssigneeTaskDetail />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}
      </div>

      {me.role === 'director' && <TabBarDirector />}
      {me.role === 'secretary' && <TabBarSecretary />}
      {me.role === 'driver' && <TabBarDriver />}
    </div>
  )
}
